import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../../../contexts/TenantContext';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import TCN from './tokens';
import Icon from './Icon';

/**
 * Tenant-constrained language switcher. Lists only the languages that the
 * tenant enabled (`tenants.enabled_languages`), defaults to all locales when
 * the column is unset. Persists the choice on `profiles.language` and calls
 * `i18n.changeLanguage` so RTL/LTR + font stack flip immediately.
 *
 * Renders as a vertical stack of tap targets (works inside a bottom sheet
 * on mobile or a popover on desktop). Pass `onAfter` to close the parent
 * surface once the user picks a language.
 */
const LANG_LABELS = {
  fr: { native: 'Français', flag: '🇫🇷' },
  en: { native: 'English', flag: '🇬🇧' },
  it: { native: 'Italiano', flag: '🇮🇹' },
  de: { native: 'Deutsch', flag: '🇩🇪' },
  ar: { native: 'العربية', flag: '🇸🇦' },
};

const LanguageMenu = ({ onAfter, compact = false }) => {
  const { i18n } = useTranslation();
  const { tenant } = useTenant();
  const { user } = useAuth();
  const [busy, setBusy] = useState(null);

  const allowed = (tenant?.enabled_languages?.length
    ? tenant.enabled_languages
    : ['en', 'fr', 'it', 'de', 'ar']
  ).filter((c) => LANG_LABELS[c]);

  const current = (i18n.language || 'en').split('-')[0];

  const handleSwitch = async (code) => {
    if (code === current || busy) {
      onAfter?.();
      return;
    }
    setBusy(code);
    try {
      await i18n.changeLanguage(code);
      if (user?.id) {
        await supabase.from('profiles').update({ language: code }).eq('id', user.id);
      }
    } finally {
      setBusy(null);
      onAfter?.();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {allowed.map((code) => {
        const label = LANG_LABELS[code];
        const isActive = code === current;
        const isBusy = busy === code;
        return (
          <button
            key={code}
            type="button"
            onClick={() => handleSwitch(code)}
            disabled={!!busy}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: compact ? '8px 10px' : '12px 14px',
              background: isActive ? `${TCN.cyan}14` : TCN.surface2,
              color: isActive ? TCN.cyan : TCN.text,
              border: `1px solid ${isActive ? `${TCN.cyan}33` : TCN.border}`,
              borderRadius: 10,
              fontFamily: TCN.fontSans,
              fontWeight: 600,
              fontSize: compact ? 13 : 14,
              cursor: busy ? 'wait' : 'pointer',
              opacity: busy && !isBusy ? 0.5 : 1,
              transition: `all ${TCN.durBase} ${TCN.ease}`,
              textAlign: 'start',
            }}
          >
            <span style={{ fontSize: compact ? 16 : 18, lineHeight: 1 }}>{label.flag}</span>
            <span style={{ flex: 1 }} dir="auto">{label.native}</span>
            {isActive && !isBusy && <Icon name="check" size={compact ? 14 : 16} />}
            {isBusy && (
              <span
                aria-hidden="true"
                style={{
                  width: 12, height: 12,
                  border: `2px solid ${TCN.cyan}`,
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.6s linear infinite',
                }}
              />
            )}
          </button>
        );
      })}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default LanguageMenu;
